import { omit } from 'lodash';

/**
 * List of global variables used on our project.
 */
import { environment } from '../environments/environment';

import {
  IDocumentOperationOptions,
  IRoundingFormatOptions,
} from '@shared/types';

import { checkSlash } from '@shared/utils/path-utils';
import { NEGATIVE_NUMBER_NEGATIVES_MAPPING } from '@app/shared/constants';
import { I18n } from '@app/i18n/strings-keys';

/**
 * Used for server request with specific delay time.
 */
export const SERVER_REQUEST_DELAY_MS: number = 1000;

/**
 * Used in XVIEntityCellRendererComponent to show pending status.
 */
export const ELLIPSIS_STRING: string = '...';

/**
 * Used for delaying form updating. E.g. We need to wait for user finished make changes in the form.
 */
export const DEFAULT_FORM_UPDATE_DELAY_MS: number = 500;

/**
 * Used for delaying error validation notifier.
 * E.g. We need to wait for user finished make changes in the form.
 */
export const ERROR_MESSAGE_DEBOUNCE: number = 1000;

/**
 * The time periods after which we remove toaster notification.
 */
export const DEFAULT_TOAST_TIMEOUTS = {
  DEFAULT_TIMEOUT: 7000,
  ERROR_TIMEOUT: 0,
  WARNING_TIMEOUT: 3000,
  SUCCESS_TIMEOUT: 4000,
  INFO_TIMEOUT: 2000,
};

/**
 * Used for delaying collapse animation. E.g. We need to wait for until animation is finished.
 */
export const COLLAPSE_ANIMATION_DURATION_MS: number = 300;

export const WEEK_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export const MEGA_BYTE = 1024 * 1024;

export const NAVIGATION_KEY_CODES = {
  KEY_LEFT: 37,
  KEY_UP: 38,
  KEY_RIGHT: 39,
  KEY_DOWN: 40,
  KEY_PAGE_HOME: 36,
  KEY_PAGE_END: 35,
  KEY_TAB: 9,
  KEY_BACKSPACE: 8,
  KEY_DELETE: 46,
};

export const BLOCK_NAVIGATION_KEY_CODES = omit(NAVIGATION_KEY_CODES, [
  'KEY_TAB',
  'KEY_LEFT',
  'KEY_RIGHT',
  'KEY_UP',
  'KEY_DOWN',
]);

export const KEY_CODES = Object.assign(
  {
    ENTER: 'Enter',
    ESCAPE: 'Escape',
    TAB: 'Tab',
    SHIFT: 'Shift',
    COMMA: ',',
    DOT: '.',
    MINUS: '-',
    PLUS: '+',
    ARROW_UP: 'ArrowUp',
    ARROW_DOWN: 'ArrowDown',
    ARROW_LEFT: 'ArrowLeft',
    ARROW_RIGHT: 'ArrowRight',
    CLEAR: 'Clear',
    BACK_SPACE: 'Backspace',
  },
  NAVIGATION_KEY_CODES,
);

export const GRID_NAVIGATION_AVAILABLE_KEYS = [
  KEY_CODES.ENTER,
  KEY_CODES.TAB,
  KEY_CODES.ARROW_UP,
  KEY_CODES.ARROW_DOWN,
  KEY_CODES.ARROW_LEFT,
  KEY_CODES.ARROW_RIGHT,
];

/**
 * ID of the root node in the grid
 */
export const GRID_ROOT_NODE_ID = 'ROOT_NODE_ID';

export const LIST_TREE_GRID_CLASS = 'list-tree-grid';

/**
 * List of permissions that are required to work with application
 */
export const APP_LEVEL_PERMISSIONS = ['VenuesInventory.View'];

/**
 * Default chunk size for infine scroll widgets
 */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * MAX_ITEMS_PER_QUERY max items entity_id to be included in query filter.
 * This constant should prevent very long query strings composition.
 */
export const MAX_ITEMS_PER_QUERY = 400;

/**
 * Default chunk size for getting max number of items
 */
export const MAX_PAGE_SIZE = 10000;

/**
 * Default chunk size for getting products with nested entity
 */
export const NESTED_PRODUCT_PAGE_SIZE = 500;

/**
 * Available permission suffixes
 */
export const PERMISSION_TYPES = {
  Add: 'Add',
  Delete: 'Delete',
  Manage: 'Manage',
  View: 'View',
  Revert: 'Revert',
};

/**
 * Default currency properties
 */
export const DEFAULT_CURRENCY_SYMBOL_POSITION = 'left';
export const DEFAULT_CURRENCY_CODE = 'USD';
export const DEFAULT_CURRENCY_SYMBOL = '$';

// amount of digits thousands
export const NUMERICAL_ORDER_AMOUNT: number = 3;

/**
 * Loader should hide after this period of time.
 * Value should not be less than 30 secs because Lambda Timeout Error can arise after this time.
 */
export const HIDE_LOADER_DELAY: number = 35000;

/**
 * Used for server request with specific function params.
 */
export const DEFAULT_DOCUMENT_OPERATION_OPTIONS: Partial<IDocumentOperationOptions> =
  {
    skipMappers: true,
    noLoader: true,
  };

/**
 * The Class Name to be applied to body container
 */
export const XUX_LIST_THEME_CLASS = 'xux-theme xux-list';
export const XUX_FORM_THEME_CLASS = 'xux-theme xux-form';

export const NO_THEME_CLASS = '';

/**
 * Uses for define widget family while form building process
 */
export enum WIDGET_FAMILY {
  XUX = 'xux',
  LEGACY = 'legacy',
}

export const DEFAULT_TEXT_FIELD = 'name';

/**
 * Uses for building form validation error on the global form level
 */
export const FORM_ROOT_PATH = '';
export enum TokenKeys {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  ACCESS_TOKEN = 'accessToken',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  PERSONAL_TOKEN = 'personalToken',
}

/*
 * Different token types
 */
/**
 * Usage ways:
 *  - As the `Authorization` header value.
 *    Put it there and {@link SessionInterceptor} will replace it with current the personal token.
 *  - As a key for Local Storage to save the token between pare refreshing
 */
export const PERSONAL_TOKEN = TokenKeys.PERSONAL_TOKEN;
/**
 * Usage ways:
 *  - As the `Authorization` header value.
 *    Put it there and {@link SessionInterceptor} will replace it with current the personal token.
 *  - As a key for Local Storage to save the token between pare refreshing
 */
export const ACCESS_TOKEN = TokenKeys.ACCESS_TOKEN;

/**
 * Usage ways:
 *  - As the lang for all application.
 */
export const COMPANY_LOCALE_FIELD = 'companyLocale';
export const USER_LOCALE_FIELD = 'userLocale';
export const USER_DEFINED_LOCALE_FIELD = 'userDefinedLocale';
export const DEFAULT_LOCALE = 'en-US';

/**
 * There is the only usage way:
 *  - Put it to the `Authorization` header to avoid adding a token and omit the `Authorization` header form the request.
 *    Notice: This behavior handled by {@link SessionInterceptor}
 */

export const NO_TOKEN = 'noToken';

export const BASE_URI = checkSlash(environment.baseURI);
export const DM_BACKEND_URI = checkSlash(environment.dmBackendURI);
export const XPRT_BACKEND_URI = checkSlash(environment.xprtBackendURI);
export const XRPT_BACKEND_URI = checkSlash(environment.xrptBackendURI);
export const XVI_BACKEND_URI = checkSlash(environment.xviBackendURI);
export const AUDIT_TRAIL_URI = checkSlash(environment.auditTrailURI);

export const ONLY_DATE_SUBSTRING_LENGTH = 10;

export const defaultCaptionPathForEntityRenderer =
  'captionForQuickFilterString';

export const ACTION_ERROR_POSTFIX = '_ERROR';
export const ACTION_SUCCESS_POSTFIX = '_SUCCESS';

export enum ACTION_STATUS {
  ERROR = 'error',
  IN_PROGRESS = 'in_progress',
  SUCCESS = 'success',
}

export enum FieldsToKeepInDocumentAfterUpdateEnum {
  ENTITY_ID = 'entity_id',
  _ID = '_id',
}

/**
 * Maximum amount of documents allowed to edit in MOMS mode
 */
export const MULTI_DOCUMENT_EDITING_MAX_DOCUMENTS_LIMIT = 50;

export const BAD_GATEWAY_STATUS_CODE = 502;
export const GATEWAY_TIMEOUT_STATUS_CODE = 504;

export const ENTITY_ID_PATH = 'entity_id';

export const ITEM_ENTITY_ID_PATH = 'item_entity_id';

export const ANY_TOKEN = 'anyToken';

/**
 * Uses as keys for all known conditions for column hide
 */
export enum HIDE_COLUMN_CONDITIONS {
  MASTER = 'master',
  ALWAYS_HIDE = 'always-hide',
}

/**
 * FORM_ERROR_SEPARATOR used to compose form validation messages
 */

export const FORM_ERROR_SEPARATOR = ' > ';

/**
 * error page title
 */

export const ERROR_PAGE_TITLE: string = 'Unexpected error';
export const BTN_PRIMARY_CLASS: string = 'btn btn-primary';
export const PDF_SIGNATURE_LINE: string = '  _______________________________  ';
export const MARK_SIGNATURE: string = 'signature';

export const DEFAULT_DECIMAL_DIVIDER: string = '.';
export const DEFAULT_THOUSANDS_DIVIDER: string = ',';
export const DEFAULT_INPUT_THOUSANDS_DIVIDER: string = '';
export const DEFAULT_NUMBER_PRECISION: number = 2;
export const DEFAULT_NEGATIVES: string = '-1.1';

export const INPUT_MAX_LENGTH: number = 50;
export const MAX_NUMBER: number = 99999999999.99;
export const MAX_INT_NUMBER: number = 99999999999;
export const MAX_NUMBER_FOR_STAND_SHEET: number = 9999999.999999;
export const BACK_END_MAX_NUMBER: number = 999999.999999;
export const MAX_INT_FOR_STAND_SHEET: number = 9999999;
export const MAX_NUMBER_FOR_TRANSFER_QUANTITY: number = 999999.99;
export const MIN_NUMBER_FOR_TRANSFER_QUANTITY: number = -999999.99;
export const MAX_INT_FOR_TRANSFER_QUANTITY: number = 999999;
export const MIN_INT_FOR_TRANSFER_QUANTITY: number = -999999;
export const MAX_INT_FOR_ORDER_QUANTITY: number = 9999;
export const MIN_INT_FOR_ORDER_QUANTITY: number = -9999;

export const MAX_SEVEN_DIGITS_NUMBER = 9999999;

export const MAX_URL_QUERY_NUMBER: number = 500;
export const MAX_SYSTEM_FILE_NAME_LENGTH: number = 244;
export const PRESERVE_SIX_DECIMALS_PRECISION: number = 6;
export const DEFAULT_DISPLAY_DECIMAL: string = '0.00';

export const DEFAULT_ROUNDING_FORMAT_OPTIONS: IRoundingFormatOptions = {
  currency: DEFAULT_CURRENCY_CODE,
  symbol: DEFAULT_CURRENCY_SYMBOL,
  symbolPositioning: DEFAULT_CURRENCY_SYMBOL_POSITION,
  thousandsSeparator: DEFAULT_THOUSANDS_DIVIDER,
  defaultFormatValue: DEFAULT_DISPLAY_DECIMAL,
  decimalSeparator: DEFAULT_DECIMAL_DIVIDER,
  negatives: NEGATIVE_NUMBER_NEGATIVES_MAPPING.Money,
  precision: DEFAULT_NUMBER_PRECISION,
  roundingMethodType: 'nearest',
  percentagePrecision: DEFAULT_NUMBER_PRECISION,
};

export enum TIME_FORMATS {
  MMM = 'MMM',
  DD = 'DD',
  DDD = 'ddd',
  L = 'L',
  ll = 'll',
  LT = 'LT',
  LTS = 'LTS',
  L_LT = 'L LT',
  L_LTS = 'L LTS',
}

export const BULK_REQUEST_COUNT = 6;

/**
 * Number input properties
 */
export const PERCENTAGE_SIGN = '%';
export const POSITION_RIGHT = 'right';
export const POSITION_LEFT = 'left';

/**
 * export & pdf constants
 */
export const MAX_PRINT_OR_EXPORT_SIZE = 50;

export const INTERVAL_FETCH_TIME: number = 10000;
export const GLOBAL_CHARGEABLE_TEXT = I18n.Common.Chargeable;

export const MAX_TASK_UPDATE_TIME = 60000;

/**
 * Time
 */
export const SECONDS = 1000;
export const MINUTES = 60 * SECONDS;
export const HOURS = 60 * MINUTES;

export const SUGGESTED_ROW_BUFFER = 100;

export const SITE_GROUP_BATCH_SIZE = 30;

export const GROUP_STARTING_COMPARE_LEVEL = 2;

/**
 * Used for quick-filter component.
 */
export const DEFAULT_FILTER_DEBOUNCE_TIME_MS: number = 1000;

export const DEFAULT_ARTIFICIAL_INPUT_THRESHOLD: number = 110;
export const MAX_SCREEN_SHOW_ITEMS: number = 50;
export const DEFAULT_ARTIFICIAL_INPUT_LENGTH: number = 8;
export const DEFAULT_VALID_SCANNING_LENGTH: number = 2;

export const DASHBOARD_TRANSFER_DATE_MAX = 90;

export const EVENT_SELECTOR_SELECT: string =
  'id,name,status,event_type_entity_id,event_days';
export const MAX_EVENT_LIMIT = 200;

export const SITE_SELECTOR_SELECT: string[] = [
  'name',
  'id',
  'type',
  'business_type',
  'store_number',
  'metadata',
];
export const XUX_DASHBOARD_THEME_CLASS = 'xux-dashboard';

/**
 * Header Panel 55px + Warning Message Box Padding 30px +  Panel Heading Padding 10px + Math.ceil(Panel Heading : 77.14px) + grid action panel: 62px + panel body padding top: 10px = 245px
 * 245px + UI buffering 5px = 250px
 */
export const DEFAULT_ITEM_LIST_TABLE_HEIGHT = 'calc(100vh - 250px)';

export enum THEMES {
  XENIAL = 'xenial',
  GENIUS = 'genius',
}

export const DEFAULT_THEME = THEMES.XENIAL;

export const XENIAL_POS_NAME = I18n.Application.XenialPOS;
export const GENIUS_POS_NAME = I18n.Application.GeniusEnterprisePOS;

export const REST_SITES_LEVEL_NAME = 'Everything Else';

export const CANCEL_ACTION = 'cancel';
export const DEFAULT_GRID_MIN_HEIGHT = '320px';

/**
 * ARRAY_OF_OBJECT_SEPARATOR used to split the path into paths to array and the value in it
 */

export const NESTED_FIELD_SEPARATOR = '.';
export const ARRAY_OF_OBJECT_PATH_SEPARATOR = '[]';
export const TRANSLATION_OBJECT_FIELD = 'i18n';
export const ORIGINAL_VALUE_FIELD = 'original';
export const MAX_CLONE_DEPTH = 9;

export const HTTP_SUCCESS_STATUS_LOWER_BOUND = 200;
export const HTTP_SUCCESS_STATUS_UPPER_BOUND = 300;

export const DEFAULT_LOAD = 1;
